import type { Ref } from 'vue';
import { ref } from 'vue';
import { defineStore } from 'pinia';
import type { DealroomFile } from '@getaccept/dsr-shared-new/src/files/types/dealroom-file';
import type { LoadFilesPayload } from '@getaccept/dsr-shared-new/src/files/types/load-files-payload';
import type { Channel } from '@getaccept/pusher';
import { DealroomEvent } from '@getaccept/pusher';
import { useToast } from '@getaccept/lib-shared-new/src/toast';
import { t } from '@getaccept/lib-shared-new/src/helpers/translation.helper';
import { FileConstants } from '@getaccept/dsr-shared-new/src/files/constants/file.constants';
import { FileStatus } from '@getaccept/dsr-shared-new/src/files/types/enums/file-status';
import { FileService } from '../../../api/files/services/file.service';
import { pusherClient } from '../../../api/pusher/pusher-client';

export const usePublishedFileStore = defineStore('filePublished', () => {
  const loading = ref(false);
  const error = ref(false);
  const files: Ref<DealroomFile[]> = ref([]);
  const totalFiles = ref(0);

  const totalFilesToUpload = ref(0);
  const filesUploaded = ref(0);
  const fileIdsToBeUploaded: Ref<string[]> = ref([]);

  const filter: Ref<LoadFilesPayload> = ref(FileConstants.defaultPayload);

  const load = async () => {
    loading.value = true;
    try {
      const { files: loadedFiles, totalCount } = await FileService.load(filter.value);
      files.value = loadedFiles ?? [];
      totalFiles.value = totalCount;
    } catch {
      error.value = true;
    } finally {
      loading.value = false;
    }
  };

  const updateFileName = async (fileId: string, name: string) => {
    try {
      await FileService.update(fileId, { fileName: name });
      files.value = files.value.map(file => (file.id === fileId ? { ...file, name } : file));
    } catch {
      error.value = true;
    }
  };

  const deleteFile = async (fileId: string) => {
    try {
      await FileService.delete(fileId);
      files.value = files.value.filter(file => file.id !== fileId);
      totalFiles.value = Math.max(totalFiles.value - 1, 0);
    } catch {
      error.value = true;
    }
  };

  const upload = async (payload: File[]) => {
    try {
      totalFilesToUpload.value = payload.length;
      await Promise.all(
        payload.map(async (uploadPayload: File) => {
          const { fileId, presignedLink } = await FileService.getUploadLink(uploadPayload);
          fileIdsToBeUploaded.value.push(fileId);
          await FileService.upload(presignedLink, uploadPayload);
          filesUploaded.value += 1;
        })
      );
    } catch (e) {
      error.value = true;
      totalFilesToUpload.value = Math.max(totalFilesToUpload.value - 1, 0);
      useToast().toast.danger(t('files_could_not_upload_file', { filename: e }));
    } finally {
      load();
      totalFilesToUpload.value = 0;
      filesUploaded.value = 0;
      useToast().toast.success(t('files_success_file_uploaded'));
    }
  };

  const handleFileUploaded = ({ fileId, size }: { fileId: string; size: number }) => {
    files.value = files.value.map(file =>
      file.id === fileId ? { ...file, status: FileStatus.Uploaded, size } : file
    );
    fileIdsToBeUploaded.value = fileIdsToBeUploaded.value.filter(id => id !== fileId);
  };

  const addFileUploadedListener = (channel: Channel) => {
    pusherClient.listen(channel, DealroomEvent.FileUploaded, payload => {
      fileIdsToBeUploaded.value.includes(payload.fileId) ? handleFileUploaded(payload) : load();
    });
  };

  const updateFilter = (payload: Partial<LoadFilesPayload>) => {
    filter.value = { ...filter.value, ...payload };
    load();
  };

  return {
    load,
    updateFileName,
    deleteFile,
    upload,
    totalFiles,
    totalFilesToUpload,
    filesUploaded,
    fileIdsToBeUploaded,
    handleFileUploaded,
    addFileUploadedListener,
    updateFilter,

    loading,
    error,
    files,
    filter,
  };
});
